import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useParams } from "react-router-dom";
import {
  FiAlertTriangle,
  FiAlertOctagon,
  FiTarget,
  FiCheckCircle,
  FiDownload,
  FiActivity,
  FiUpload,
} from "react-icons/fi";
import {
  MdOutlineWarehouse,
  MdOutlineCampaign,
  MdOutlineLocalActivity,
} from "react-icons/md";
import { BiTask } from "react-icons/bi";
import { TbBuildingWarehouse } from "react-icons/tb";
import jsPDF from "jspdf";

const PDF = {
  colors: {
    primary: [67, 53, 167],
    success: [16, 185, 129],
    warning: [245, 158, 11],
    danger: [239, 68, 68],
    orange: [249, 115, 22],
    dark: [15, 23, 42],
    mid: [71, 85, 105],
    light: [148, 163, 184],
    bg: [248, 250, 252],
    white: [255, 255, 255],
    border: [226, 232, 240],
    riskBg: [254, 242, 242],
    warnBg: [255, 247, 237],
    missBg: [240, 249, 255],
  },
};

const setColor = (pdf, rgb, alpha = 1) => pdf.setTextColor(...rgb);
const setFill = (pdf, rgb) => pdf.setFillColor(...rgb);
const setDraw = (pdf, rgb) => pdf.setDrawColor(...rgb);
const bold = (pdf, size) => { pdf.setFont(undefined, "bold"); if (size) pdf.setFontSize(size); };
const normal = (pdf, size) => { pdf.setFont(undefined, "normal"); if (size) pdf.setFontSize(size); };

function drawRoundedCard(pdf, x, y, w, h, fillRgb, strokeRgb, r = 3) {
  setFill(pdf, fillRgb);
  setDraw(pdf, strokeRgb);
  pdf.setLineWidth(0.4);
  pdf.roundedRect(x, y, w, h, r, r, "FD");
}

function drawProgressBar(pdf, x, y, w, h, percent, filledRgb) {
  setFill(pdf, PDF.colors.border);
  setDraw(pdf, PDF.colors.border);
  pdf.setLineWidth(0);
  pdf.roundedRect(x, y, w, h, h / 2, h / 2, "F");
  if (percent > 0) {
    const filled = Math.max((percent / 100) * w, h);
    setFill(pdf, filledRgb);
    pdf.roundedRect(x, y, filled, h, h / 2, h / 2, "F");
  }
}

function drawStatusBadge(pdf, x, y, label, bgRgb, textRgb = PDF.colors.white) {
  const pad = 3;
  normal(pdf, 7);
  const tw = pdf.getTextWidth(label);
  setFill(pdf, bgRgb);
  setDraw(pdf, bgRgb);
  pdf.roundedRect(x, y - 4.5, tw + pad * 2, 6, 1.5, 1.5, "F");
  setColor(pdf, textRgb);
  pdf.text(label, x + pad, y);
}

function sectionHeader(pdf, text, y, margin, pageWidth, iconColor = PDF.colors.primary) {
  setFill(pdf, iconColor);
  pdf.rect(margin, y - 4, 3, 10, "F");
  bold(pdf, 13);
  setColor(pdf, PDF.colors.dark);
  pdf.text(text, margin + 6, y + 2);
  setDraw(pdf, PDF.colors.border);
  pdf.setLineWidth(0.3);
  pdf.line(margin + 6 + pdf.getTextWidth(text) + 4, y, pageWidth - margin, y);
  return y + 12;
}

const EventDashboard = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await axiosInstance.get(`/dashboard/event/${id}`);
      setData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const generatePDF = async () => {
    if (!data) return;
    setIsGeneratingPDF(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const PW = pdf.internal.pageSize.getWidth();
      const PH = pdf.internal.pageSize.getHeight();
      const M = 14;
      const inner = PW - M * 2;
      let y = M;

      const newPage = () => { pdf.addPage(); y = M; };
      const guard = (need) => { if (y + need > PH - M - 10) newPage(); };

      setFill(pdf, PDF.colors.dark);
      pdf.rect(0, 0, PW, 38, "F");
      setFill(pdf, PDF.colors.primary);
      pdf.rect(0, 36, PW, 3, "F");

      bold(pdf, 20);
      setColor(pdf, PDF.colors.white);
      pdf.text("Event Control Dashboard", M, 18);

      normal(pdf, 9);
      setColor(pdf, [148, 163, 200]);
      pdf.text("Daily Operations Report", M, 26);

      const dateStr = new Date(data.generatedAt).toLocaleString();
      normal(pdf, 8);
      const dw = pdf.getTextWidth(dateStr) + 8;
      setFill(pdf, [30, 41, 70]);
      pdf.roundedRect(PW - M - dw, 12, dw, 10, 2, 2, "F");
      setColor(pdf, [148, 163, 200]);
      pdf.text(dateStr, PW - M - dw / 2, 18.5, { align: "center" });

      y = 50;

      const kpis = [
        { label: "Real Progress", value: `${data.progress.global.realPercent}%`, color: PDF.colors.primary },
        { label: "Planned", value: `${data.progress.global.plannedPercent}%`, color: PDF.colors.success },
        { label: "Delay vs Plan", value: `${data.progress.global.delayPercent}%`, color: PDF.colors.warning },
        { label: "Stands At Risk", value: `${data.standsAtRisk.length}`, color: PDF.colors.orange },
        { label: "Critical HSE", value: `${data.hseCriticalOpen}`, color: PDF.colors.danger },
        { label: "Missing Today", value: `${(data.missingToday || []).length}`, color: [14, 165, 233] },
      ];

      const kw = (inner - 5 * 3) / 6;
      const kh = 22;

      kpis.forEach((k, i) => {
        const kx = M + i * (kw + 3);
        drawRoundedCard(pdf, kx, y, kw, kh, PDF.colors.white, k.color, 2.5);
        setFill(pdf, k.color);
        pdf.roundedRect(kx, y, kw, 2.5, 1, 1, "F");
        bold(pdf, 14);
        setColor(pdf, k.color);
        pdf.text(k.value, kx + kw / 2, y + 13, { align: "center" });
        normal(pdf, 6.5);
        setColor(pdf, PDF.colors.mid);
        pdf.text(k.label, kx + kw / 2, y + 19, { align: "center" });
      });

      y += kh + 10;

      y = sectionHeader(pdf, "Global Progress", y, M, PW, PDF.colors.primary);

      const barH = 7;
      const labelW = 40;
      const barW = inner - labelW - 20;

      const progressRows = [
        { label: "Real Progress", val: data.progress.global.realPercent, color: PDF.colors.primary },
        { label: "Planned Progress", val: data.progress.global.plannedPercent, color: PDF.colors.success },
      ];

      progressRows.forEach((row) => {
        guard(16);
        normal(pdf, 8.5);
        setColor(pdf, PDF.colors.mid);
        pdf.text(row.label, M, y + barH - 1);
        bold(pdf, 9);
        setColor(pdf, PDF.colors.dark);
        pdf.text(`${row.val}%`, M + labelW + barW + 4, y + barH - 1);
        drawProgressBar(pdf, M + labelW, y, barW, barH, row.val, row.color);
        y += 14;
      });

      y += 4;

      guard(50);
      y = sectionHeader(pdf, "Progress by Hall", y, M, PW, PDF.colors.primary);

      const hallEntries = Object.entries(data.progress.byHall).sort(([a], [b]) => {
        const na = parseInt(a.match(/\d+/)?.[0] || 0, 10);
        const nb = parseInt(b.match(/\d+/)?.[0] || 0, 10);
        return na - nb;
      });

      const colW4 = inner / 4;

      normal(pdf, 7.5);
      setColor(pdf, PDF.colors.light);
      ["Hall", "Real %", "Planned %", "Status"].forEach((h, i) => {
        pdf.text(h, M + i * colW4, y);
      });
      y += 2;
      setDraw(pdf, PDF.colors.border);
      pdf.setLineWidth(0.2);
      pdf.line(M, y, PW - M, y);
      y += 5;

      hallEntries.forEach(([hall, prog]) => {
        guard(10);
        const isDelayed = prog.status === "delayed";
        if (isDelayed) {
          setFill(pdf, [255, 251, 245]);
          pdf.rect(M, y - 4, inner, 8, "F");
        }
        normal(pdf, 8.5);
        setColor(pdf, PDF.colors.dark);
        pdf.text(hall, M, y);
        pdf.text(`${prog.realPercent}%`, M + colW4, y);
        pdf.text(`${prog.plannedPercent}%`, M + colW4 * 2, y);
        drawStatusBadge(
          pdf, M + colW4 * 3, y,
          prog.status === "on_track" ? "ON TRACK" : "DELAYED",
          isDelayed ? PDF.colors.danger : PDF.colors.success
        );
        y += 8;
      });

      y += 6;

      guard(50);
      y = sectionHeader(pdf, "Progress by Tent", y, M, PW, PDF.colors.primary);

      const tentEntries = Object.entries(data.progress.byTent);

      normal(pdf, 7.5);
      setColor(pdf, PDF.colors.light);
      ["Tent", "Real %", "Planned %", "Status"].forEach((h, i) => {
        pdf.text(h, M + i * colW4, y);
      });
      y += 2;
      setDraw(pdf, PDF.colors.border);
      pdf.setLineWidth(0.2);
      pdf.line(M, y, PW - M, y);
      y += 5;

      tentEntries.forEach(([tent, prog]) => {
        guard(10);
        const isDelayed = prog.status === "delayed";
        if (isDelayed) {
          setFill(pdf, [255, 251, 245]);
          pdf.rect(M, y - 4, inner, 8, "F");
        }
        normal(pdf, 8.5);
        setColor(pdf, PDF.colors.dark);
        pdf.text(tent, M, y);
        pdf.text(`${prog.realPercent}%`, M + colW4, y);
        pdf.text(`${prog.plannedPercent}%`, M + colW4 * 2, y);
        drawStatusBadge(
          pdf, M + colW4 * 3, y,
          prog.status === "on_track" ? "ON TRACK" : "DELAYED",
          isDelayed ? PDF.colors.danger : PDF.colors.success
        );
        y += 8;
      });

      y += 6;

      guard(50);
      y = sectionHeader(pdf, "Tickets Overview", y, M, PW, PDF.colors.warning);

      const ticketTypes = [
        { name: "Cleaning", src: "cleaning", color: PDF.colors.primary },
        { name: "Security (HSE)", src: "security", color: PDF.colors.danger },
      ];

      const tw2 = inner / 2 - 4;

      ticketTypes.forEach((tt, i) => {
        const tx = M + i * (tw2 + 8);
        const td = data.tickets[tt.src];
        guard(36);
        drawRoundedCard(pdf, tx, y, tw2, 32, PDF.colors.bg, PDF.colors.border, 3);
        bold(pdf, 9);
        setColor(pdf, PDF.colors.dark);
        pdf.text(tt.name, tx + 6, y + 9);
        const metrics = [
          { label: "Total", val: td.total, color: PDF.colors.mid },
          { label: "Open", val: td.open, color: PDF.colors.warning },
          { label: "Critical", val: td.critical, color: PDF.colors.danger },
        ];
        metrics.forEach((m, mi) => {
          const mx = tx + 6 + mi * (tw2 / 3);
          bold(pdf, 13);
          setColor(pdf, m.color);
          pdf.text(`${m.val}`, mx, y + 22);
          normal(pdf, 7);
          setColor(pdf, PDF.colors.light);
          pdf.text(m.label, mx, y + 28);
        });
      });

      y += 42;

      if (data.standsAtRisk.length > 0) {
        guard(30);
        y = sectionHeader(pdf, "Stands At Risk", y, M, PW, PDF.colors.danger);

        data.standsAtRisk.forEach((s) => {
          guard(28);
          const isRisk = s.level === "at_risk";
          const fillColor = isRisk ? PDF.colors.riskBg : PDF.colors.warnBg;
          const lineColor = isRisk ? PDF.colors.danger : PDF.colors.warning;

          drawRoundedCard(pdf, M, y, inner, 24, fillColor, lineColor, 3);
          setFill(pdf, lineColor);
          pdf.roundedRect(M, y, 3, 24, 1.5, 1.5, "F");

          bold(pdf, 9);
          setColor(pdf, PDF.colors.dark);
          pdf.text(`${s.standNumber}  –  ${s.exhibitorName}`, M + 7, y + 8);

          normal(pdf, 7.5);
          setColor(pdf, PDF.colors.mid);
          pdf.text(`Hall: ${s.hall || "—"}   Tent: ${s.tent || "—"}`, M + 7, y + 15);
          pdf.text(s.reason, M + 7, y + 21);

          drawStatusBadge(
            pdf, PW - M - 28, y + 10,
            s.level.replace("_", " ").toUpperCase(),
            lineColor
          );

          const pbx = PW - M - 65;
          const pbw = 44;
          normal(pdf, 6);
          setColor(pdf, PDF.colors.light);
          pdf.text("Real", pbx, y + 16);
          pdf.text("Plan", pbx, y + 21);
          drawProgressBar(pdf, pbx + 8, y + 13, pbw, 3, s.realPercent, PDF.colors.primary);
          drawProgressBar(pdf, pbx + 8, y + 18, pbw, 3, s.plannedPercent, PDF.colors.success);
          setColor(pdf, PDF.colors.dark);
          normal(pdf, 6);
          pdf.text(`${s.realPercent}%`, pbx + pbw + 10, y + 16);
          pdf.text(`${s.plannedPercent}%`, pbx + pbw + 10, y + 21);

          y += 28;
        });
      }

      const missing = data.missingToday || [];

      newPage();
      y = sectionHeader(pdf, "No Show", y, M, PW, [14, 165, 233]);

      if (missing.length === 0) {
        guard(16);
        drawRoundedCard(pdf, M, y, inner, 13, PDF.colors.missBg, [14, 165, 233], 3);
        bold(pdf, 9);
        setColor(pdf, [14, 165, 233]);
        pdf.text("All expected stands have arrived onsite.", M + inner / 2, y + 7.5, { align: "center" });
        y += 18;
      } else {
        guard(12);
        drawRoundedCard(pdf, M, y, inner, 10, [240, 249, 255], [14, 165, 233], 2);
        normal(pdf, 8.5);
        setColor(pdf, [14, 165, 233]);
        pdf.text(
          `${missing.length} stand${missing.length > 1 ? "s" : ""} scheduled to arrive today have not yet checked in.`,
          M + 5, y + 6.5
        );
        y += 15;

        const cols = [
          { header: "#", width: 16, align: "center" },
          { header: "Stand", width: 35, align: "left" },
          { header: "Exhibitor", width: 65, align: "left" },
          { header: "Hall", width: 25, align: "center" },
          { header: "Tent", width: 25, align: "center" },
          { header: "Size", width: 16, align: "center" },
        ];

        const rowH = 9;
        const headerH = 9;
        const pad = 3;
        const tableW = cols.reduce((s, c) => s + c.width, 0);
        const tableX = (PW - tableW) / 2;

        const colX = [];
        let cx0 = tableX;
        cols.forEach((col) => { colX.push(cx0); cx0 += col.width; });

        guard(headerH + 2);
        setFill(pdf, PDF.colors.dark);
        pdf.rect(tableX, y, tableW, headerH, "F");

        cols.forEach((col, i) => {
          bold(pdf, 6.5);
          setColor(pdf, PDF.colors.white);
          const tx = col.align === "center" ? colX[i] + col.width / 2 : colX[i] + pad;
          pdf.text(col.header, tx, y + 6, { align: col.align === "left" ? "left" : "center" });
        });
        y += headerH;

        missing.forEach((s, idx) => {
          guard(rowH + 1);
          setFill(pdf, idx % 2 === 0 ? PDF.colors.white : PDF.colors.bg);
          pdf.rect(tableX, y, tableW, rowH, "F");

          const cells = [
            { value: `${idx + 1}`, colIdx: 0 },
            { value: s.standNumber || "—", colIdx: 1 },
            { value: s.exhibitorName || "—", colIdx: 2 },
            { value: s.hall || "—", colIdx: 3 },
            { value: s.tentNumber || "—", colIdx: 4 },
            { value: s.size || "—", colIdx: 5 },
          ];

          cells.forEach(({ value, colIdx }) => {
            const col = cols[colIdx];
            normal(pdf, 7.5);
            setColor(pdf, PDF.colors.dark);
            const maxW = col.width - pad * 2;
            let text = String(value);
            if (pdf.getTextWidth(text) > maxW) {
              while (pdf.getTextWidth(text + "…") > maxW && text.length > 0) {
                text = text.slice(0, -1);
              }
              text = text + "…";
            }
            const tx = col.align === "center" ? colX[colIdx] + col.width / 2 : colX[colIdx] + pad;
            pdf.text(text, tx, y + 6, { align: col.align === "left" ? "left" : "center" });
          });

          setDraw(pdf, PDF.colors.border);
          pdf.setLineWidth(0.15);
          pdf.line(tableX, y + rowH, tableX + tableW, y + rowH);
          y += rowH;
        });

        setDraw(pdf, [14, 165, 233]);
        pdf.setLineWidth(0.4);
        pdf.rect(tableX, y - rowH * missing.length - headerH, tableW, rowH * missing.length + headerH, "S");
        y += 8;
      }

      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        setFill(pdf, PDF.colors.dark);
        pdf.rect(0, PH - 12, PW, 12, "F");
        normal(pdf, 7);
        setColor(pdf, [100, 116, 139]);
        pdf.text("Event Control Dashboard — Daily Report", M, PH - 5);
        pdf.text(`Page ${i} / ${pageCount}`, PW / 2, PH - 5, { align: "center" });
        pdf.text(new Date(data.generatedAt).toLocaleDateString(), PW - M, PH - 5, { align: "right" });
      }

      pdf.save(`Event_Report_${id}_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF report");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const downloadStandsAtRiskCSV = () => {
    if (!data?.standsAtRisk?.length) return;

    const na = "N/A";
    const headers = ["Stand Number", "Exhibitor Name", "Company Contractor", "Contractor Name", "Contractor Phone"];

    const rows = data.standsAtRisk.map(s => [
      s.standNumber || na,
      s.exhibitorName || na,
      s.contractor?.companyName || na,
      s.contractor?.chiefName || na,
      s.contractor?.phone || na,
    ]);

    const csvContent = "\uFEFF" + [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Stands_At_Risk_${id}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#4335A7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { progress, tickets, standsAtRisk, hseCriticalOpen, badgesOnsite, missingToday = [], onsite } = data;
  const global = progress.global;

  const progressPie = [
    { name: "Completed", value: global.realPercent },
    { name: "Remaining", value: 100 - global.realPercent },
  ];

  const ticketBarData = [
    { name: "Cleaning", Open: tickets.cleaning.open, Critical: tickets.cleaning.critical },
    { name: "HSE", Open: tickets.security.open, Critical: tickets.security.critical },
  ];

  const COLORS = {
    primary: "#4335A7",
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
    secondary: "#6366f1",
    sky: "#0ea5e9",
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* HEADER */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">Event Control Dashboard</h1>
              <p className="text-gray-500 text-sm">Real-time monitoring and analytics</p>
            </div>
            <button
              onClick={generatePDF}
              disabled={isGeneratingPDF}
              className="bg-gradient-to-r from-[#4335A7] to-[#5842c7] text-white px-6 py-3 rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingPDF ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <FiDownload className="w-5 h-5" />
                  Download Daily Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* GLOBAL PROGRESS */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-[#4335A7] to-[#5842c7] rounded-lg flex items-center justify-center">
              <FiActivity className="text-white text-xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Global Progress</h2>
          </div>
          <div className="space-y-6">
            <EnhancedProgressBar label="Real Progress" value={global.realPercent} color={COLORS.primary} icon={<FiTarget />} />
            <EnhancedProgressBar label="Planned Progress" value={global.plannedPercent} color={COLORS.success} icon={<FiCheckCircle />} />
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard title="Delay vs Plan" value={`${global.delayPercent}%`} icon={<FiAlertTriangle />} color="bg-amber-50" textColor="text-amber-600" />
          <MetricCard title="Stands At Risk" value={standsAtRisk.length} icon={<FiAlertOctagon />} color="bg-orange-50" textColor="text-orange-600" />
          <MetricCard
            title={`Badges Onsite ${badgesOnsite?.type ? `(${badgesOnsite.type.replace("_", " ").toUpperCase()})` : ""}`}
            value={badgesOnsite ? `${badgesOnsite.inside}/${badgesOnsite.total}` : "0/0"}
            icon={<MdOutlineLocalActivity />}
            color="bg-emerald-50"
            textColor="text-emerald-600"
          />
          <MetricCard title="Critical HSE Open" value={hseCriticalOpen} icon={<FiAlertOctagon />} color="bg-red-50" textColor="text-red-600" danger />
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-sm">
                <FiAlertTriangle className="text-white text-xl" />
              </div>

              <div>
                <h2 className="text-xl font-bold text-gray-900">Stands At Risk</h2>
                <p className="text-sm text-gray-500">Requires immediate attention</p>
              </div>
            </div>

            {standsAtRisk.length > 0 && (
              <button
                onClick={downloadStandsAtRiskCSV}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-semibold text-sm shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
              >
                <FiDownload className="w-4 h-4" />
                Download CSV
              </button>
            )}
          </div>

          {/* Content */}
          {standsAtRisk.length === 0 ? (
            <div className="text-center py-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
              <FiCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
              <p className="text-green-700 font-semibold text-lg">All systems operational!</p>
              <p className="text-green-600 text-sm mt-1">No stands at risk detected</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-y border-slate-100">
                    {["Stand", "Exhibitor", "Zone", "Progress", "Delay"].map((h) => (
                      <th
                        key={h}
                        className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {standsAtRisk.map((s) => {
                    const real = s.realPercent ?? 0;
                    const delay = Math.max(0, (s.plannedPercent ?? 0) - real);
                    const barColor =
                      s.level === "at_risk" ? "bg-red-500" : s.level === "warning" ? "bg-orange-500" : "bg-amber-400";

                    return (
                      <tr key={s.standId} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 font-bold text-slate-900">{s.standNumber}</td>
                        <td className="px-6 py-4">
                          <p className="text-slate-700">{s.exhibitorName}</p>
                          {s.reason && <p className="text-xs text-slate-400 mt-0.5">{s.reason}</p>}
                        </td>
                        <td className="px-6 py-4 text-slate-600">{s.hall || s.tent || "—"}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-2 w-28 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${real}%` }} />
                            </div>
                            <span className="text-xs font-semibold text-slate-700 w-9">{real}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-semibold text-red-600">
                          {s.delayDays > 0 ? `-${s.delayDays} days` : `-${s.delayPercent}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CHARTS */}
        <div className="grid md:grid-cols-2 gap-6">
          <ChartCard title="Progress by Hall" icon={<MdOutlineWarehouse />}>
            <BarChart data={Object.entries(progress.byHall).map(([hall, val]) => ({ hall, Real: val.realPercent, Planned: val.plannedPercent }))}>
              <XAxis dataKey="hall" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }} />
              <Legend wrapperStyle={{ paddingTop: "20px" }} />
              <Bar dataKey="Real" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
              <Bar dataKey="Planned" fill={COLORS.success} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Progress by Tent" icon={<MdOutlineCampaign />}>
            <BarChart data={Object.entries(progress.byTent).map(([tent, val]) => ({ tent, Real: val.realPercent, Planned: val.plannedPercent }))}>
              <XAxis dataKey="tent" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }} />
              <Legend wrapperStyle={{ paddingTop: "20px" }} />
              <Bar dataKey="Real" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
              <Bar dataKey="Planned" fill={COLORS.success} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Tickets Overview" icon={<BiTask />}>
            <BarChart data={ticketBarData}>
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }} />
              <Legend wrapperStyle={{ paddingTop: "20px" }} />
              <Bar dataKey="Open" fill={COLORS.warning} radius={[8, 8, 0, 0]} />
              <Bar dataKey="Critical" fill={COLORS.danger} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Completion Rate" icon={<FiTarget />}>
            <PieChart>
              <Pie data={progressPie} outerRadius={100} dataKey="value" label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                <Cell fill={COLORS.primary} />
                <Cell fill="#E5E7EB" />
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ChartCard>
        </div>

        {onsite && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <OnsiteCard
              title="Global Onsite Stands"
              arrived={onsite.global.arrived}
              total={onsite.global.total}
            />
            <OnsiteCard
              title="Today Onsite Stands"
              arrived={onsite.today.arrived}
              total={onsite.today.expected}
              isToday
            />
            <CheckinCard
              title="Today Total Check-ins"
              count={onsite.today.checkedIn}
            />
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-600 rounded-lg flex items-center justify-center">
              <FiAlertTriangle className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">No Show</h2>
              <p className="text-sm text-gray-500">Stands scheduled to arrive but not yet checked in</p>
            </div>
          </div>

          {missingToday.length === 0 ? (
            <div className="text-center py-12 bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl border-2 border-sky-200">
              <FiCheckCircle className="text-6xl text-sky-500 mx-auto mb-4" />
              <p className="text-sky-700 font-semibold text-lg">All expected stands have arrived!</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-4 py-3 text-center font-semibold w-10">#</th>
                    <th className="px-4 py-3 text-left font-semibold">Stand Number</th>
                    <th className="px-4 py-3 text-left font-semibold">Exhibitor Name</th>
                    <th className="px-4 py-3 text-center font-semibold">Hall</th>
                    <th className="px-4 py-3 text-center font-semibold">Tent</th>
                    <th className="px-4 py-3 text-center font-semibold">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {missingToday.map((s, idx) => (
                    <tr
                      key={s.standId}
                      className={`border-t border-slate-100 transition-colors duration-150 hover:bg-sky-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                    >
                      <td className="px-4 py-3 text-center text-slate-400 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3 font-bold text-gray-900">{s.standNumber || "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{s.exhibitorName || "—"}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{s.hall || "—"}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{s.tentNumber || "—"}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{s.size || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const OnsiteCard = ({ title, arrived, total, isToday = false }) => {
  const percent = total > 0 ? Math.round((arrived / total) * 100) : 0;
  const allArrived = arrived >= total && total > 0;
  const accent = isToday
    ? allArrived ? "from-emerald-500 to-emerald-600" : "from-amber-400 to-amber-500"
    : "from-violet-500 to-violet-700";
  const ringColor = isToday
    ? allArrived ? "border-emerald-200" : "border-amber-200"
    : "border-violet-200";
  const barColor = isToday
    ? allArrived ? "bg-emerald-500" : "bg-amber-400"
    : "bg-violet-500";
  const textAccent = isToday
    ? allArrived ? "text-emerald-600" : "text-amber-500"
    : "text-violet-600";

  return (
    <div className={`bg-white rounded-2xl shadow-lg border-2 ${ringColor} p-6 flex flex-col gap-4`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 bg-gradient-to-br ${accent} rounded-xl flex items-center justify-center shadow-md`}>
          <TbBuildingWarehouse className="text-white text-xl" />
        </div>
        <p className="font-semibold text-gray-700 text-sm">{title}</p>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-5xl font-black ${textAccent} leading-none`}>{arrived}</span>
        <span className="text-2xl font-bold text-slate-300 mb-1">/ {total}</span>
      </div>
      <div>
        <div className="flex justify-between text-xs text-slate-400 mb-1.5">
          <span>{percent}% arrived</span>
          <span>{total - arrived} pending</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${barColor}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const CheckinCard = ({ title, count }) => (
  <div className="bg-white rounded-2xl shadow-lg border-2 border-sky-200 p-6 flex flex-col gap-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center shadow-md">
        <TbBuildingWarehouse className="text-white text-xl" />
      </div>
      <p className="font-semibold text-gray-700 text-sm">{title}</p>
    </div>
    <div className="flex items-end gap-2">
      <span className="text-5xl font-black text-sky-500 leading-none">{count ?? 0}</span>
      <span className="text-sm font-medium text-slate-400 mb-2">stands</span>
    </div>
    <p className="text-xs text-slate-400">Stands checked in today</p>
  </div>
);

const MetricCard = ({ title, value, icon, danger, trend, color = "bg-white", textColor = "text-gray-900" }) => (
  <div className={`${color} rounded-xl shadow-md p-5 border border-slate-200 transition-all duration-200 hover:shadow-lg hover:-translate-y-1`}>
    <div className="flex justify-between items-start mb-3">
      <span className={`text-2xl ${textColor}`}>{icon}</span>
      {trend && <span className={`text-xs font-semibold ${trend === "up" ? "text-green-600" : "text-red-600"}`}>{trend === "up" ? "↗" : "↘"}</span>}
    </div>
    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide mb-2">{title}</p>
    <p className={`text-3xl font-bold ${danger ? "text-red-600" : textColor}`}>{value}</p>
  </div>
);

const EnhancedProgressBar = ({ label, value, color, icon }) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-2">
        <span className="text-gray-600">{icon}</span>
        <span className="font-semibold text-gray-700">{label}</span>
      </div>
      <span className="text-xl font-bold text-gray-900">{value}%</span>
    </div>
    <div className="relative w-full bg-gray-200 rounded-full h-6 overflow-hidden shadow-inner">
      <div
        className="h-6 rounded-full transition-all duration-700 ease-out relative overflow-hidden"
        style={{ width: `${value}%`, background: `linear-gradient(90deg, ${color}, ${color}dd)` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, icon, children }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200 hover:shadow-xl transition-shadow duration-200">
    <div className="flex items-center gap-2 mb-6">
      <span className="text-2xl text-gray-700">{icon}</span>
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
    </div>
    <ResponsiveContainer width="100%" height={300}>{children}</ResponsiveContainer>
  </div>
);

export default EventDashboard;